/**
 * Safe2Pay 3DS Authentication Service
 *
 * Handles 3DS (3D Secure) authentication for credit card payments.
 * Documentation: https://developers.safe2pay.com.br/reference/referenciando-o-script
 */

import { logFrontendError } from 'scripts/errorLogger';

const SCRIPT_LOAD_TIMEOUT = 10000;
const SCRIPT_CHECK_INTERVAL = 100;
const AUTH_TIMEOUT = 60000;

/**
 * Check if Safe2Pay script is loaded
 */
function isSafe2PayLoaded() {
    return typeof window.Safe2Pay !== 'undefined' &&
           typeof window.Safe2Pay.Mpi !== 'undefined';
}

/**
 * Wait for Safe2Pay script to load
 */
function waitForSafe2PayScript() {
    return new Promise((resolve, reject) => {
        function rejectWithLoadError() {
            const error = new Error('Safe2Pay script failed to load');
            logFrontendError(error.message, { tag: '3ds' });
            reject(error);
        }

        if (isSafe2PayLoaded()) {
            resolve();
            return;
        }

        if (window.__safe2payLoadError) {
            rejectWithLoadError();
            return;
        }

        const startTime = Date.now();
        const checkInterval = setInterval(() => {
            if (isSafe2PayLoaded()) {
                clearInterval(checkInterval);
                resolve();
            } else if (window.__safe2payLoadError) {
                clearInterval(checkInterval);
                rejectWithLoadError();
            } else if (Date.now() - startTime > SCRIPT_LOAD_TIMEOUT) {
                clearInterval(checkInterval);
                rejectWithLoadError();
            }
        }, SCRIPT_CHECK_INTERVAL);
    });
}

/**
 * Fix Cardinal's z-index issues by watching for their elements
 * and manually adjusting inline styles
 */
function fixCardinalZIndex() {
    // Use MutationObserver to detect when Cardinal elements are added
    const observer = new MutationObserver(() => {
        const modal = document.querySelector('#Cardinal-Modal, .cardinalOverlay-content');
        const overlay = document.querySelector('#Cardinal-Overlay, .cardinalOverlay-mask');

        if (modal && overlay) {
            // Force correct z-index via inline styles (only way to override Cardinal's inline !important)
            overlay.style.setProperty('z-index', '9998', 'important');
            modal.style.setProperty('z-index', '9999', 'important');

            // Stop observing once we've fixed it
            observer.disconnect();
        }
    });

    // Start observing
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

    // Also try to fix immediately in case elements already exist
    setTimeout(() => {
        const modal = document.querySelector('#Cardinal-Modal, .cardinalOverlay-content');
        const overlay = document.querySelector('#Cardinal-Overlay, .cardinalOverlay-mask');

        if (modal && overlay) {
            overlay.style.setProperty('z-index', '9998', 'important');
            modal.style.setProperty('z-index', '9999', 'important');
        }
    }, 100);

    // Cleanup after 10 seconds
    setTimeout(() => observer.disconnect(), 10000);
}

/**
 * Initialize Safe2Pay MPI with configuration
 * @param {number} amount - Transaction amount in cents or as decimal
 * @param {boolean} isSandbox - Whether to use sandbox environment
 */
export function initializeSafe2Pay(amount, isSandbox = true) {
    if (!isSafe2PayLoaded()) {
        throw new Error('Safe2Pay script not loaded');
    }

    const config = {
        IsEnabled: true,
        IsSandbox: isSandbox,
        IsDebug: isSandbox,
        IsChallengeSuppressed: false
    };

    window.Safe2Pay.Mpi.Init(config, amount);

    // Start watching for Cardinal elements to fix z-index
    fixCardinalZIndex();
}

/**
 * Parse expiration date from MM/YYYY format
 * @param {string} date - Date in MM/YYYY format
 * @returns {{ month: string, year: string }}
 */
function parseExpirationDate(date) {
    const parts = date.split('/');
    return {
        month: parts[0].padStart(2, '0'),
        year: parts[1]
    };
}

/**
 * Build payment object for Safe2Pay 3DS authentication
 * @param {object} creditcardData - Credit card form data
 * @param {object} receiptData - Receipt form data
 * @param {object} user - User context data
 * @param {number} orderAmount - Order total amount
 */
function buildPaymentObject(creditcardData, receiptData, user, orderAmount) {
    // Validate required data
    if (!creditcardData?.creditcardNumber) {
        throw new Error('Card number is missing');
    }
    if (!creditcardData?.date) {
        throw new Error('Expiration date is missing');
    }

    const { month, year } = parseExpirationDate(creditcardData.date);

    // Ensure card number is clean digits only
    const cleanCardNumber = String(creditcardData.creditcardNumber).replace(/\D/g, '');

    if (cleanCardNumber.length < 13 || cleanCardNumber.length > 19) {
        console.warn('[Safe2Pay 3DS] Card number length unusual:', cleanCardNumber.length);
    }

    const paymentObject = {
        // Required fields
        currency: 'BRL',
        transactionvalue: orderAmount,
        installments: String(creditcardData.installments || 1).padStart(2, '0'),
        cardnumber: cleanCardNumber,
        cardexpirationmonth: month,
        cardexpirationyear: year,
        cardalias: creditcardData.holder || '',

        // Customer identification
        billto_customerid: user?.id ? String(user.id) : '',
        billto_contactname: `${receiptData?.firstName || ''} ${receiptData?.lastName || ''}`.trim(),
        billto_phonenumber: receiptData?.phone?.replace(/\D/g, '') || '',
        billto_email: user?.email || '',

        // Order information
        order_productcode: 'PHY', // Physical goods/tickets
        order_recurrence: false,

        // Shipping same as billing for digital products
        shipto_sameasbillto: 'true'
    };

    // Add billing address if available from user
    if (user?.address) {
        paymentObject.billto_street1 = user.address.street || '';
        paymentObject.billto_street2 = user.address.complement || '';
        paymentObject.billto_city = user.address.city || '';
        paymentObject.billto_state = user.address.state || '';
        paymentObject.billto_zipcode = user.address.zipcode?.replace(/\D/g, '') || '';
        paymentObject.billto_country = 'BR';
    }

    return paymentObject;
}

/**
 * Main 3DS authentication function
 * @param {object} creditcardData - Credit card form data
 * @param {object} receiptData - Receipt form data
 * @param {object} user - User context data
 * @param {number} orderAmount - Order total amount
 * @returns {Promise<object>} 3DS authentication result
 */
export async function authenticate3DS(creditcardData, receiptData, user, orderAmount) {
    // Wait for script to be available
    await waitForSafe2PayScript();

    // Determine environment from Vite
    const isSandbox = import.meta.env.MODE !== 'production';

    // Initialize Safe2Pay
    initializeSafe2Pay(orderAmount, isSandbox);

    // Build payment object before setting up listeners
    const paymentObject = buildPaymentObject(creditcardData, receiptData, user, orderAmount);

    return new Promise((resolve, reject) => {

        const Mpi = window.Safe2Pay.Mpi;

        // Track whether checkout flow has started
        let checkoutStarted = false;
        let settled = false;

        // Cardinal's challenge iframe communicates with the vendor script via
        // its own internal postMessage handshake, invisible to this app. If
        // it silently breaks (ad blocker, in-app browser blocking third-party
        // iframes/cookies, dropped network mid-challenge, user abandoning the
        // challenge), none of the Mpi events below ever fire and this promise
        // would otherwise hang forever, leaving the checkout loader stuck.
        const timeoutId = setTimeout(() => {
            if (settled) return;
            settled = true;
            const message = 'Authentication timed out waiting for Cardinal challenge response';
            console.error(`[Safe2Pay 3DS] ${message}`);
            logFrontendError(message, { tag: '3ds' });
            reject({
                success: false,
                error: 'AUTHENTICATION_TIMEOUT',
                message: 'A autenticacao do cartao demorou demais. Tente novamente.'
            });
        }, AUTH_TIMEOUT);

        function settledResolve(result) {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            resolve(result);
        }

        function settledReject(error) {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            reject(error);
        }

        // Success - authentication completed
        Mpi.addEventListener('onSuccess', (e) => {
            settledResolve({
                success: true,
                cavv: e.Cavv,
                xid: e.Xid,
                eci: e.Eci,
                version: e.Version,
                referenceId: e.ReferenceId
            });
        });

        // Ready - script loaded and token validated
        // IMPORTANT: Only call Checkout after onReady fires
        Mpi.addEventListener('onReady', () => {
            checkoutStarted = true;
            try {
                Mpi.Checkout(paymentObject);
            } catch (error) {
                console.error('[Safe2Pay 3DS] Checkout() threw synchronously:', error);
                logFrontendError(`Checkout() threw synchronously: ${error?.message || error}`, {
                    stack: error?.stack,
                    tag: '3ds'
                });
                settledReject({
                    success: false,
                    error: 'AUTHENTICATION_ERROR',
                    message: 'Erro durante autenticacao. Tente novamente.'
                });
            }
        });

        // Failure - authentication failed (card eligible but failed)
        Mpi.addEventListener('onFailure', (e) => {
            settledReject({
                success: false,
                error: 'AUTHENTICATION_FAILED',
                message: 'Falha na autenticacao do cartao. Verifique seus dados e tente novamente.',
                eci: e.Eci,
                xid: e.Xid,
                version: e.Version,
                referenceId: e.ReferenceId
            });
        });

        // Unenrolled - card not eligible for 3DS
        Mpi.addEventListener('onUnenrolled', (e) => {
            settledReject({
                success: false,
                error: 'CARD_UNENROLLED',
                message: 'Este cartao nao e elegivel para autenticacao 3DS. Por favor, tente outro cartao.',
                eci: e.Eci,
                xid: e.Xid,
                version: e.Version,
                referenceId: e.ReferenceId
            });
        });

        // Disabled - merchant disabled authentication
        Mpi.addEventListener('onDisabled', () => {
            settledReject({
                success: false,
                error: 'AUTHENTICATION_DISABLED',
                message: 'Autenticacao 3DS desabilitada.'
            });
        });

        // Error - system error during authentication
        Mpi.addEventListener('onError', (e) => {
            // If checkout hasn't started yet, this is an initialization error - ignore it
            // MPI900 errors during Init() are non-fatal warnings
            if (!checkoutStarted && e.ReturnCode === 'MPI900') {
                console.warn('[Safe2Pay 3DS] MPI900 initialization error - ignoring as non-fatal');
                return;
            }

            // If checkout has started, or it's a different error code, reject the promise
            console.error('[Safe2Pay 3DS] Authentication error:', e.ReturnCode, e.ReturnMessage);
            logFrontendError(`Authentication error ${e.ReturnCode}: ${e.ReturnMessage}`, { tag: '3ds' });
            settledReject({
                success: false,
                error: 'AUTHENTICATION_ERROR',
                message: e.ReturnMessage || 'Erro durante autenticacao. Tente novamente.',
                returnCode: e.ReturnCode,
                xid: e.Xid,
                version: e.Version,
                referenceId: e.ReferenceId
            });
        });

        // Unsupported brand - card brand not supported
        Mpi.addEventListener('onUnsupportedBrand', () => {
            settledReject({
                success: false,
                error: 'UNSUPPORTED_BRAND',
                message: 'Bandeira do cartao nao suportada para autenticacao 3DS.'
            });
        });
    });
}

/**
 * Check if 3DS feature flag is enabled. Awaits `window.featuresReady` first —
 * `window.features` is populated by a fetch kicked off at app boot, and
 * reading it immediately (before that fetch resolves) could report the flag
 * as off even when it's genuinely enabled.
 * @returns {Promise<boolean>}
 */
export async function is3DSEnabled() {
    await window.featuresReady;
    return window.features?.includes('checkout-3ds') || false;
}
